import { EanQuantityBundle } from "../../../components/EanQuantityBundle";
import { HelpText } from "../../../components/HelpText";
import { QuantityBadge } from "../../../components/QuantityBadge";
import styles from "./EanSearchSection.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEraser, faMagnifyingGlass, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import { EANView } from "../../../components/EANView";

interface Props {
  newData: string[];
  eanField: string;
  eanTotals: Record<string, number>;
  getEANType: (ean: string) => string;
  confirmClear: boolean;
  formError: string;
  isSyncing: boolean;
  isErrorExiting: boolean;
  hasNotFound: boolean;
  actions: {
    addEan: (ean: string) => void;
    removeEan: (ean: string) => void;
    setEanField: (value: string) => void;
    handleClearClick: () => void;
    find: () => void;
    removeNotFound: () => void;
  };
}

export const EanSearchSection = ({
  newData,
  eanField,
  eanTotals,
  getEANType,
  confirmClear,
  actions,
  formError,
  isSyncing,
  isErrorExiting,
  hasNotFound
}: Props) => {
  const sortedData = [...newData].sort((a, b) => {
    const charA = a.slice(-4);
    const charB = b.slice(-4);
    if (charA === charB) return BigInt(a) > BigInt(b) ? 1 : -1;
    return charA.localeCompare(charB);
  });
  let lastGroupChar = "";
  const useGrouping = sortedData.length > 10;

  return (
    <div className={styles.container}>
      <div className={styles.searchList}>
        {sortedData.map((ean) => {
          const groupChar = ean.slice(-4, -3);
          const showHeader = useGrouping && groupChar !== lastGroupChar;
          lastGroupChar = groupChar;
          const totalQuantity = eanTotals[ean];
          const isFound = totalQuantity > 0;

          return (
            <React.Fragment key={ean}>
              {showHeader && <div className={styles.digitHeader}>...{groupChar}___</div>}
              <EanQuantityBundle key={ean} onClick={() => actions.removeEan(ean)} className={styles[getEANType(ean)]}>
                <EANView ean={ean} />
                {isFound && (
                  <QuantityBadge
                    status={getEANType(ean) === "selected" ? "special" : getEANType(ean) === "" ? "neutral" : "plus"}
                  >
                    {totalQuantity}
                  </QuantityBadge>
                )}
              </EanQuantityBundle>
            </React.Fragment>
          );
        })}
      </div>
      <div className={styles.wrapper}>
        <input
          name="ean"
          // maxLength={4}
          value={eanField}
          onChange={(e) => actions.setEanField(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              actions.addEan(eanField);
            }
          }}
          inputMode="decimal"
          placeholder="EAN"
        />
        <button className={styles.addButton} onClick={() => actions.addEan(eanField)} title="Dodaj EAN">
          <FontAwesomeIcon icon={faPlus} />
        </button>
        <button className={`${styles.searchButton}`} onClick={actions.find} disabled={isSyncing} title="Znajdź">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
        {hasNotFound && (
          <button className={styles.removeNotFoundButton} onClick={actions.removeNotFound} title="Usuń nieznalezione">
            <FontAwesomeIcon icon={faEraser} />
          </button>
        )}
        {newData.length > 0 && (
          <button
            className={`${styles.clearButton} ${confirmClear ? styles.confirming : ""}`}
            onClick={actions.handleClearClick}
            title="Wyczyść listę"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}
      </div>
      {formError && (
        <div className={`${styles.errorMessage} ${isErrorExiting ? styles.errorExit : ""}`}>{formError}</div>
      )}

      {newData.length === 0 && (
        <HelpText alwaysVisible>
          Wpisz szukany EAN (minimum cztery ostatnie cyfry) i zatwierdź niebieskim przyciskiem, aby dodać do szukanej
          listy.
        </HelpText>
      )}
    </div>
  );
};
